import { useRef, useEffect, useState, useCallback } from 'react';
import type { Element, ElementPatch } from '@/components/canvas/document/publicTypes';
import { lineGeometryToPatch } from '@/components/canvas/elements/shape/line/lineElementPatch';

export type LineHandleCoordinateSpace = 'page' | 'workspace';

export interface LineHandleState {
  id: string;
  handle: 'start' | 'end';
  coordinateSpace: LineHandleCoordinateSpace;
  startX: number;
  startY: number;
  startEndX: number;
  startEndY: number;
  startClientX: number;
  startClientY: number;
}

export function useLineHandle(
  pageToCanvas: (clientX: number, clientY: number) => { x: number; y: number },
  worldToCanvas: (clientX: number, clientY: number) => { x: number; y: number },
  updateElementSilent: (id: string, patch: ElementPatch) => void,
  commitElement: (id: string, patch: ElementPatch) => void,
) {
  const [lineHandleState, setLineHandleState] = useState<LineHandleState | null>(null);
  const [lineHandlePreview, setLineHandlePreview] = useState<{
    x: number;
    y: number;
    endX: number;
    endY: number;
  } | null>(null);
  const lineHandlePreviewRef = useRef<{
    x: number;
    y: number;
    endX: number;
    endY: number;
    width: number;
    height: number;
  } | null>(null);

  const pageToCanvasRef = useRef(pageToCanvas);
  pageToCanvasRef.current = pageToCanvas;
  const worldToCanvasRef = useRef(worldToCanvas);
  worldToCanvasRef.current = worldToCanvas;

  const toCanvas = useCallback((clientX: number, clientY: number, space: LineHandleCoordinateSpace) => {
    return space === 'workspace'
      ? worldToCanvasRef.current(clientX, clientY)
      : pageToCanvasRef.current(clientX, clientY);
  }, []);

  useEffect(() => {
    if (!lineHandleState) return;
    const { id, handle, coordinateSpace, startX, startY, startEndX, startEndY } = lineHandleState;

    const onMove = (e: MouseEvent) => {
      const p = toCanvas(e.clientX, e.clientY, coordinateSpace);
      let patch: ElementPatch<'line'>;
      if (handle === 'start') {
        const endX = startX + startEndX - p.x;
        const endY = startY + startEndY - p.y;
        const width = Math.max(1, Math.abs(endX));
        const height = Math.max(1, Math.abs(endY));
        lineHandlePreviewRef.current = { x: p.x, y: p.y, endX, endY, width, height };
        setLineHandlePreview({ x: p.x, y: p.y, endX, endY });
        patch = lineGeometryToPatch(p.x, p.y, endX, endY, width, height);
      } else {
        const endX = p.x - startX;
        const endY = p.y - startY;
        const width = Math.max(1, Math.abs(endX));
        const height = Math.max(1, Math.abs(endY));
        lineHandlePreviewRef.current = { x: startX, y: startY, endX, endY, width, height };
        setLineHandlePreview({ x: startX, y: startY, endX, endY });
        patch = lineGeometryToPatch(startX, startY, endX, endY, width, height);
      }
      updateElementSilent(id, patch);
    };

    const onUp = () => {
      if (lineHandlePreviewRef.current) {
        const { x, y, endX, endY, width, height } = lineHandlePreviewRef.current;
        commitElement(id, lineGeometryToPatch(x, y, endX, endY, width, height));
      }
      setLineHandleState(null);
      setLineHandlePreview(null);
      lineHandlePreviewRef.current = null;
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [lineHandleState, toCanvas, updateElementSilent, commitElement]);

  return { lineHandleState, setLineHandleState, lineHandlePreview, setLineHandlePreview };
}
